import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { computeDelta, formatDelta } from '@/features/reports/lib/delta'
import { cn } from '@/lib/utils'

interface DeltaBadgeProps {
  current: number
  previous: number | null | undefined
  /** What the comparison is against, already worded — "vs. Vorperiode", "vs. Do 27.08." */
  againstLabel?: string
  /**
   * `inverse` for the badge sitting on an inverted card. Not cosmetic: the default red is #b91c1c,
   * which on a near-black ground stops being a colour and takes the direction cue with it.
   */
  tone?: 'default' | 'inverse'
  className?: string
}

/**
 * Change against the comparison window (CHG-2026-09-manager-reporting).
 *
 * The arrow is not decoration. Direction is carried by the icon as well as the colour, because a
 * green-red pair is exactly the distinction a red-green colourblind reader cannot make — and this
 * is the one number on the page whose whole content is a direction.
 *
 * "Nothing to compare against" renders as an em dash rather than as ±0 %: a store that was closed
 * last Sunday did not stay level, it was shut.
 */
export function DeltaBadge({
  current,
  previous,
  againstLabel,
  tone = 'default',
  className,
}: DeltaBadgeProps) {
  const { t } = useTranslation('reports')
  const delta = computeDelta(current, previous)
  const value = formatDelta(delta)

  const Icon =
    delta.kind === 'up' ? ArrowUp : delta.kind === 'down' ? ArrowDown : Minus
  const inverse = tone === 'inverse'
  const directionClass =
    delta.kind === 'up'
      ? inverse
        ? 'text-delta-up-inverse'
        : 'text-delta-up'
      : delta.kind === 'down'
        ? inverse
          ? 'text-delta-down-inverse'
          : 'text-delta-down'
        : inverse
          ? 'text-background/70'
          : 'text-muted-foreground'
  const mutedClass = inverse ? 'text-background/70' : 'text-muted-foreground'

  if (delta.kind === 'unknown') {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs',
          mutedClass,
          className,
        )}
      >
        <span aria-hidden="true">—</span>
        <span>{t('delta.noComparison')}</span>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-1.5 text-xs', className)}>
      <Icon
        className={cn('h-3.5 w-3.5 shrink-0', directionClass)}
        aria-hidden="true"
      />
      <span className={cn('font-semibold tabular-nums', directionClass)}>
        {value ?? t('delta.fromZero')}
      </span>
      {againstLabel ? (
        // Hidden on phones: it is the same phrase under all four KPI tiles, and spelling it out
        // there wraps every tile onto a second line for information the period header already gives.
        <span className={cn('hidden sm:inline', mutedClass)}>
          {againstLabel}
        </span>
      ) : null}
      <span className="sr-only">
        {delta.kind === 'up'
          ? t('delta.up')
          : delta.kind === 'down'
            ? t('delta.down')
            : t('delta.flat')}
      </span>
    </div>
  )
}
